import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, FolderKanban, MessageSquareWarning, PhoneCall,
  Users2, FileText, ClipboardList, LogOut, Building2, ChevronDown, ShieldCheck, Ticket, BookOpen, Settings as SettingsIcon, MessagesSquare, Download, BarChart3, Handshake, Menu, X,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import { Badge } from "@/components/ui/badge";
import NotificationBell from "@/components/NotificationBell";
import { useBranding } from "@/hooks/useBranding";
import OfflineBanner from "@/components/OfflineBanner";
import DKFooter from "@/components/DKFooter";
import LanguageThemeSelector from "@/components/LanguageThemeSelector";
import { usePWAInstall } from "@/context/PWAInstallContext";

const NAV_ITEMS = [
  { to: "/", key: "dashboard", icon: LayoutDashboard, testid: "nav-dashboard", end: true },
  { to: "/dossiers", key: "dossiers", icon: FolderKanban, testid: "nav-dossiers" },
  { to: "/reclamations", key: "reclamations", icon: MessageSquareWarning, testid: "nav-reclamations" },
  { to: "/appels", key: "appels", icon: PhoneCall, testid: "nav-appels" },
  { to: "/discussions", key: "chat", icon: MessagesSquare, testid: "nav-discussions" },
  { to: "/messagerie", key: "dm", icon: MessagesSquare, testid: "nav-messagerie" },
  { to: "/file-attente", key: "tickets", icon: Ticket, testid: "nav-file-attente" },
  { to: "/clients", key: "clients", icon: Users2, testid: "nav-clients" },
  { to: "/ged", key: "ged", icon: FileText, testid: "nav-ged" },
  { to: "/base-connaissances", key: "kb", icon: BookOpen, testid: "nav-kb" },
  { to: "/historique", key: "history", icon: ClipboardList, testid: "nav-historique" },
];

const ADMIN_NAV_ITEMS = [
  { to: "/analytics", key: "analytics", icon: BarChart3, testid: "nav-analytics" },
  { to: "/prospects", key: "prospects", icon: Handshake, testid: "nav-prospects" },
  { to: "/utilisateurs", key: "users", icon: ShieldCheck, testid: "nav-users" },
  { to: "/parametres", key: "settings", icon: SettingsIcon, testid: "nav-parametres" },
];

export default function AppLayout() {
  const { user, logout } = useAuth();
  const { branding } = useBranding();
  const { t } = useTranslation();
  const { canInstall, promptInstall } = usePWAInstall();
  const navigate = useNavigate();
  const [open, setOpen] = useState(true);
  const [mobileOpen, setMobileOpen] = useState(false);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  const initials = (user?.name || "?")
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase();

  return (
    <div
      className="min-h-screen flex bg-[#F6F7FB] dark:bg-slate-950 text-slate-900 dark:text-slate-100"
      style={{ background: "var(--dkc-bg, #F6F7FB)" }}
    >
      {/* Sidebar — desktop (>=md) */}
      <aside
        className={`hidden md:flex flex-col ${open ? "w-64" : "w-20"} shrink-0 border-r border-slate-200 dark:border-slate-800 bg-[#0B1B4B] text-slate-100 transition-[width] duration-300`}
        data-testid="app-sidebar"
      >
        <SidebarContent
          open={open}
          branding={branding}
          user={user}
          t={t}
          onNavClick={undefined}
          onToggle={() => setOpen((v) => !v)}
          showCollapseBtn
        />
      </aside>

      {/* Sidebar — mobile drawer (<md) */}
      {mobileOpen && (
        <>
          <div
            className="md:hidden fixed inset-0 bg-black/50 backdrop-blur-sm z-40"
            onClick={() => setMobileOpen(false)}
            data-testid="mobile-drawer-backdrop"
            aria-hidden
          />
          <aside
            className="md:hidden fixed left-0 top-0 bottom-0 w-72 z-50 flex flex-col bg-[#0B1B4B] text-slate-100 shadow-2xl animate-in slide-in-from-left duration-200"
            data-testid="mobile-drawer"
          >
            <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
              <div className="font-display font-bold text-lg tracking-tight">Menu</div>
              <button
                onClick={() => setMobileOpen(false)}
                className="w-9 h-9 rounded-lg text-white hover:bg-white/10 grid place-items-center"
                data-testid="mobile-drawer-close"
                aria-label="Fermer le menu"
              >
                <X className="w-5 h-5" />
              </button>
            </div>
            <SidebarContent
              open={true}
              branding={branding}
              user={user}
              t={t}
              onNavClick={() => setMobileOpen(false)}
            />
          </aside>
        </>
      )}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <OfflineBanner />
        <header className="h-16 border-b border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 flex items-center justify-between px-4 md:px-8 sticky top-0 z-30">
          <div className="flex items-center gap-3">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden w-10 h-10 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 grid place-items-center -ml-1"
              aria-label="Ouvrir le menu"
              data-testid="mobile-menu-toggle"
            >
              <Menu className="w-5 h-5 text-slate-700 dark:text-slate-200" />
            </button>
            <div className="md:hidden flex items-center gap-2">
              <div className="w-8 h-8 rounded-lg bg-[#0B1B4B] grid place-items-center overflow-hidden shrink-0">
                {branding?.logo_data_url ? (
                  <img src={branding.logo_data_url} alt="Logo" className="w-full h-full object-contain p-0.5" />
                ) : (
                  <Building2 className="w-4 h-4 text-white" />
                )}
              </div>
              <div className="font-display font-bold text-blue-900 dark:text-blue-300 truncate max-w-[120px] text-sm">{branding?.company_name || "dkconnect"}</div>
            </div>
            <div className="hidden md:block">
              <div className="text-xs uppercase tracking-[0.18em] text-slate-400 dark:text-slate-500">Espace</div>
              <div className="font-display text-lg font-semibold text-slate-900 dark:text-slate-100 -mt-0.5">{user?.service || "Général"}</div>
            </div>
          </div>

          <div className="flex items-center gap-2 md:gap-3">
            <LanguageThemeSelector variant="header" />
            <NotificationBell />
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  className="flex items-center gap-2 pl-2 pr-3 py-1.5 rounded-full border border-slate-200 dark:border-slate-700 hover:bg-slate-50 dark:hover:bg-slate-800"
                  data-testid="header-user-menu"
                >
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-800 to-violet-700 text-white grid place-items-center text-xs font-semibold">
                    {initials}
                  </div>
                  <div className="text-left hidden sm:block">
                    <div className="text-xs font-semibold text-slate-900 dark:text-slate-100 leading-tight">{user?.name}</div>
                    <div className="text-[10px] uppercase tracking-widest text-slate-400 dark:text-slate-500 leading-tight">{user?.role}</div>
                  </div>
                  <ChevronDown className="w-4 h-4 text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <DropdownMenuLabel>
                  <div className="text-sm font-semibold">{user?.name}</div>
                  <div className="text-xs text-slate-500">{user?.email}</div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem>
                  <Badge variant="outline" className="mr-2">{user?.role}</Badge>
                  <span className="text-slate-600 dark:text-slate-300">{user?.service || "—"}</span>
                </DropdownMenuItem>
                {canInstall && (
                  <>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      onClick={() => promptInstall()}
                      data-testid="menu-install-app"
                    >
                      <Download className="w-4 h-4 mr-2" /> {t("common.install_app")}
                    </DropdownMenuItem>
                  </>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem onClick={onLogout} data-testid="logout-btn" className="text-red-600 focus:text-red-700">
                  <LogOut className="w-4 h-4 mr-2" /> {t("common.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>

        <main className="flex-1 px-4 md:px-8 py-6 md:py-8 overflow-x-hidden">
          <Outlet />
        </main>
        <DKFooter variant="app" />
      </div>
    </div>
  );
}


// ---------- SidebarContent (shared between desktop + mobile drawer) ----------

function SidebarContent({ open, branding, user, t, onNavClick, onToggle, showCollapseBtn }) {
  const navClass = ({ isActive }) =>
    `flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm ${
      isActive
        ? "bg-white/12 text-white border border-white/10"
        : "text-blue-100/80 hover:text-white hover:bg-white/8 border border-transparent"
    }`;

  return (
    <>
      <div className="px-4 py-5 flex items-center gap-3 border-b border-white/10">
        <div className="w-9 h-9 rounded-lg bg-white/10 grid place-items-center backdrop-blur-md overflow-hidden shrink-0">
          {branding?.logo_data_url ? (
            <img src={branding.logo_data_url} alt="Logo" className="w-full h-full object-contain" />
          ) : (
            <Building2 className="w-5 h-5 text-white" />
          )}
        </div>
        {open && (
          <div className="min-w-0">
            <div className="font-display font-bold text-lg tracking-tight leading-none truncate" data-testid="brand-name">{branding?.company_name || "dkconnect"}</div>
            <div className="text-[10px] uppercase tracking-[0.25em] text-blue-200 mt-1 truncate">{branding?.support_name || t("brand.tagline")}</div>
          </div>
        )}
      </div>

      <nav className="sidebar-nav flex-1 px-3 py-4 space-y-1 overflow-y-auto">
        {NAV_ITEMS.map((n) => (
          <NavLink
            key={n.to}
            to={n.to}
            end={n.end}
            data-testid={n.testid}
            onClick={onNavClick}
            className={navClass}
          >
            <n.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
            {open && <span className="truncate">{t(`nav.${n.key}`)}</span>}
          </NavLink>
        ))}

        {user?.role === "admin" && (
          <>
            <div className={`px-3 mt-6 mb-2 text-[10px] uppercase tracking-[0.25em] text-blue-300/70 ${!open && "hidden"}`}>
              Administration
            </div>
            {ADMIN_NAV_ITEMS.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={n.testid}
                onClick={onNavClick}
                className={navClass}
              >
                <n.icon className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
                {open && <span className="truncate">{t(`nav.${n.key}`)}</span>}
              </NavLink>
            ))}
          </>
        )}

        {user?.role === "directeur" && (
          <>
            <div className={`px-3 mt-6 mb-2 text-[10px] uppercase tracking-[0.25em] text-blue-300/70 ${!open && "hidden"}`}>
              Administration
            </div>
            <NavLink
              to="/analytics"
              data-testid="nav-analytics"
              onClick={onNavClick}
              className={navClass}
            >
              <BarChart3 className="w-[18px] h-[18px] shrink-0" strokeWidth={1.75} />
              {open && <span className="truncate">{t("nav.analytics")}</span>}
            </NavLink>
          </>
        )}
      </nav>

      {showCollapseBtn && (
        <div className="p-3 border-t border-white/10">
          <button
            className="w-full text-left text-xs text-blue-200/70 hover:text-white px-2 py-1"
            onClick={onToggle}
            data-testid="sidebar-toggle"
          >
            {open ? "◄ Réduire" : "►"}
          </button>
        </div>
      )}
    </>
  );
}
